import React, { useState } from "react";
import { CCol, CFormGroup, CInput, CLabel, CRow, CTextarea } from "@coreui/react";
import { INVOICE_EXPORT_FIELDS, InvoiceExportData } from "../../../models/invoice";
import { COUNTRIES } from "../../../models/countries";
import { INCOTERMS } from "../../../models/incoterms";
import { getFieldErrors } from "../../../models/errors";
import { FieldErrors } from "../../form/FieldErrors";
import AnimatedCheckbox from "../../checkbox/AnimatedCheckbox";
import SelectBootstrap from "../../react-select-bootstrap/SelectBootstrap";
import { useInvoiceFormContext } from "../context/InvoiceFormContext";

const countries = COUNTRIES.filter((country) => country.code !== "PRY" && country.code.length === 3);
const labelStyle = { fontWeight: 600, fontSize: "0.875rem", color: "var(--theme-text-soft)" };

export const InvoiceFormExportSection: React.FC = () => {
  const { invoiceRef, errors, readonly } = useInvoiceFormContext();
  const [data, setData] = useState<InvoiceExportData | null>(invoiceRef.current?.exportData ?? null);
  const change = (next: InvoiceExportData | null) => {
    if (invoiceRef.current) invoiceRef.current.exportData = next;
    setData(next);
  };
  const changeField = (key: keyof InvoiceExportData, value: string) => {
    if (data) change({ ...data, [key]: value });
  };
  return (
    <section className="my-4">
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <CLabel className="mb-0" htmlFor="invoice-export" style={labelStyle}>
              Factura de exportación
            </CLabel>
          </CCol>
          <CCol md={2}>
            <CRow>
              <AnimatedCheckbox id="invoice-export" value={data !== null} disabled={readonly}
                onChange={(checked) => change(checked ? {
                  operationType: "Exportación", negotiationCondition: "", destinationCountry: "",
                  freightCompany: "", transportAgent: "", paymentInstructions: "",
                  billOfLadingNumbers: "", internationalManifestNumbers: "",
                } : null)} />
            </CRow>
          </CCol>
        </CRow>
      </CFormGroup>
      {data && <>
        <div style={{ display: "flex", alignItems: "center", margin: "16px 0" }}>
          <div style={{ width: "4px", height: "18px", background: "#006657", borderRadius: "2px", marginRight: "10px", flexShrink: 0 }} />
          <i className="fa fa-globe text-primary mr-2" style={{ fontSize: "0.85rem" }} />
          <h6 style={{ margin: 0, fontWeight: 600, color: "var(--theme-muted)", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.8px" }}>Datos de exportación</h6>
          <div style={{ flex: 1, height: "1px", background: "var(--theme-divider)", marginLeft: "10px" }} />
        </div>
        <CRow>
          {INVOICE_EXPORT_FIELDS.map(([key, label], index) => (
            <CCol md={6} key={key}>
              <CFormGroup>
                <CRow className="align-items-center">
                  <CCol md={4}>
                    <CLabel className="mb-0" htmlFor={`export-${key}`} style={labelStyle}>
                      {index < 8 && <span className="text-danger mr-1">*</span>}{label}
                    </CLabel>
                  </CCol>
                  <CCol md={8}>
                    {key === "destinationCountry" ? (
                      <SelectBootstrap inputId={`export-${key}`} placeholder="Seleccione un país" options={countries}
                        value={countries.find((country) => country.code === data[key] || country.description.toLowerCase() === data[key].toLowerCase()) ?? null}
                        getOptionValue={(country) => country.code} getOptionLabel={(country) => country.description}
                        isDisabled={readonly} isClearable
                        onChange={(country) => changeField(key, country?.code ?? "")} />
                    ) : key === "negotiationCondition" ? (
                      <SelectBootstrap inputId={`export-${key}`} placeholder="Seleccione un Incoterm" options={INCOTERMS}
                        value={INCOTERMS.find((item) => item.code === data[key].toUpperCase()) ?? null}
                        getOptionValue={(item) => item.code} getOptionLabel={(item) => `${item.code} - ${item.description}`}
                        isDisabled={readonly} isClearable
                        onChange={(item) => changeField(key, item?.code ?? "")} />
                    ) : ["paymentInstructions", "freightDetails", "additionalInformation"].includes(key) ? (
                      <CTextarea id={`export-${key}`} value={data[key] ?? ""} rows={2} placeholder={label}
                        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => changeField(key, event.target.value)} />
                    ) : (
                      <CInput id={`export-${key}`} value={data[key] ?? ""} placeholder={label}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => changeField(key, event.target.value)} />
                    )}
                    <FieldErrors errors={getFieldErrors(`exportData.${key}`, errors) as string[]} />
                  </CCol>
                </CRow>
              </CFormGroup>
            </CCol>
          ))}
        </CRow>
      </>}
      <FieldErrors errors={getFieldErrors("exportData", errors) as string[]} />
    </section>
  );
};
